//#!/usr/bin/env node
/**
 * BTC TX Builder
 * - bip66 / Strict DER signing
 * - bip112 / Checksequenceverify
 * - bip125 / RBF
 * - bip136 / Bech32 TX
 * - bip141-143-147 / Segregated Witness
 * - bip174 / PSBTV v0
 * - bip197-199 / Hashed Time-Locked Contract / Transactions
 * - bip327 / MuSig2
 * - bip340 / Schnorr Signatures
 * - bip341-342 / Taproot SegWit v1 spending rules / Validation
 * - bip350 / Bech32m format for v1+ witness addresses
 * - bip370 / PSBTV v2
 * - bip371 / PSBT Taproot fields
 * - bip373 MuSig2 PSBT
 * - bip380-381-382-383-385-386-387 / Output/Non-SegWit/Segwit/Multisig script Descriptors
 */

import {sha256} from '@noble/hashes/sha256'
import {ripemd160} from '@noble/hashes/ripemd160'
import { base58 } from '@scure/base';
import { bech32, bech32m } from 'bech32'

import Wallet from '@ramsi88/btc-signer'

import API from './lib/Api.js'

const OP_0 = 0x00
const OP_1 = 0x51

const OP_PUSHDATA1 = 0x4c
const OP_PUSHDATA2 = 0x4d

const OP_IF = 0x63
const OP_NOTIF = 0x64
const OP_ELSE = 0x67
const OP_ENDIF = 0x68

const OP_VERIFY = 0x69
const OP_RETURN = 0x6a

const OP_DROP = 0x75
const OP_DUP = 0x76

const OP_AND = 0x84
const OP_OR = 0x85
const OP_XOR = 0x86
const OP_EQUAL = 0x87
const OP_EQUALVERIFY = 0x88

const OP_RIPEMD160 = 0xa6
const OP_SHA256 = 0xa8
const OP_HASH160 = 0xa9
const OP_HASH256 = 0xaa
const OP_CODESEPARATOR = 0xab

const OP_CHECKSIG = 0xac
const OP_CHECKSIGVERIFY = 0xad
const OP_CHECKMULTISIG = 0xae
const OP_CHECKMULTISIGVERIFY = 0xaf
const OP_CHECKSIGADD = 0xba

const OP_CHECKLOCKTIMEVERIFY = 0xb1
const OP_CHECKSEQUENCEVERIFY = 0xb2

/**
 * P2PK     scriptPubKey: <pubKey> OP_CHECKSIG | scriptSig: <sig>
 * P2PKH    scriptPubKey: OP_DUP OP_HASH160 <pubKeyHash> OP_EQUALVERIFY OP_CHECKSIG | scriptSig: <sig> <pubKey>
 * P2MS     scriptPubKey: M <pubKey1> ... <pubKeyN> OP_CHECKMULTISIG | scriptSig: OP_0 <sig1> <sig2>
 * P2WPKH   scriptPubKey: OP_DUP OP_HASH160 <pubKeyHash> OP_EQUALVERIFY OP_CHECKSIG | scriptSig: <signature> <pubkey> OP_CHECKSIG | witness: 0 <publicKeyHash> (20bytes redeem)
 * P2SH     scriptPubKey: OP_HASH160 <scriptHash> OP_EQUAL/VERIFY | scriptSig: <sig1> <sig2> 2 <pubKey1> ... <pubKeyN> 5 OP_CHECKMULTISIG
 * P2WSH    scriptPubKey: 0 <scriptHash> | scriptSig: <sig1> <sig2> 2 <pubKey1> ... <pubKeyN> 5 OP_CHECKMULTISIG | witness:  0 <sha256RedeemScript> (32bytes redeem)
 * DATA     scriptPubKey: OP_RETURN <0 to 40 bytes of data> | scriptSig:
 */
export class TX {

    /**
     * Bitcoin transaction instance
     * @param {*} version Specify 1 or 2
     * @param {*} segwit  true or false for segwit
     */
    constructor(network="bitcoin",version=2,segwit=false,type="p2pkh") {
        /**
         * - Version 4byte
         * - VINS [inputCount 1+byte + inputs 32byte + vout 4byte + scriptSig(unlockScript) ?byte + sequence 4byte ...]
         * - VOUTS [outputCount 1+byte + value 8byte + scriptPubKey(len 1byte + pubkey) ...]
         * - LOCKTIME 4byte
         */
        this.debugger = false
        this.type = type // p2pkh | p2sh | p2wpkh | p2wsh
        this.txid = []
        this.wtxid = []
        this.version = new Uint8Array([version,0x00,0x00,0x00])
        this.flag = segwit? new Uint8Array([0x01]) : new Uint8Array([0x00])
        this.inputs = [] // ScriptSig
        this.vins = [] // ScriptSig
        this.vouts = [] // ScriptPubKey
        this.sequence = new Uint8Array(([0xFF,0xFF,0xFF,0xFD])) // locktime 0xFFFFFFFD | rbf 0xFFFFFFFE | rel locktime 0x80000000
        this.locktime = new Uint8Array([0x00,0x00,0x00,0x00])
        this.sighash = new Uint8Array([0x01]) // 0x01, 0x02, 0x03, 0x81, 0x82 and 0x83 

        this.inputCount = 0
        this.outputCount = 0

        this.signature = []
        this.signatures = []
        this.scriptSig = []
        this.wtxid = [] //[nVersion][marker][flag][txins][txouts][witness][nLockTime]
        this.witnessCount = 0
        this.witness = []
        this.redeem = []
        
        //
        this.segwit = segwit
        this.network = network
        this.fee = 1000
        this.size = 0
        this.vsize = 0
        this.weight = 0

        this.amount = []
        this.output = []
        this.outputScript = []

        this.unsignedtx = {
            // version + inputcount + vins + outputcount + vouts + locktime
            // vin = [txid + vout + scriptsig + sequence] // scriptsig = [ 00 + <>]
            // vouts = [ amount + scriptpubkey]
            version: this.version,
            inputcount: [],
            vins: [],
            outputcount: [],
            vouts: [],
            locktime : this.locktime
        }
        this.txtosign = {
            // version + inputcount + vins + vouts + locktime + sighash
            // vin = [txid + vout + scriptsig + sequence] // scriptsig = [ ?? + <prevout scriptpubkey>]
            // vouts = [ amount + scriptpubkey]
            version: this.version,
            inputcount: new Uint8Array([this.inputCount]),
            vins: [],
            outputcount: new Uint8Array([this.outputCount]),
            vouts: [],
            locktime : this.locktime,
            sighash: new Uint8Array([this.sighash,0x00,0x00,0x00]),
        }
        this.signedtx = {
            // version + inputcount + vins + vouts + locktime
            // vin = [txid + vout + scriptsig + sequence] // scriptsig = [ ?? + <script ...sig ...pub>]
            // vouts = [ amount + scriptpubkey]
            version: this.version,
            inputcount: new Uint8Array([this.inputCount]),
            vins: [],
            outputcount: new Uint8Array([this.outputCount]),
            vouts: [],
            locktime : this.locktime
        }
    }

    /**
     * Create an input given the hash of an unspent transaction output and the script pubkey (locking script)
     * @param {*} utxo txid
     * @param {*} script script or pubkey 
     * @param {*} type p2pkh|p2sh
     * @param {*} n index to spend
     * @returns this
     */
    createInput(utxo="",script="",type="p2pkh",n=0) {
        /**
         * Inputs Count
         * Inputs[tx_id, vout, script_sig, sequence]
         */
        const redeem = this.scripter(type,script)
        const txId = new Uint8Array(32);
        txId.set(Buffer.from(utxo,'hex').reverse())
        this.txid = txId
        //console.log("[TX_ID]:",Buffer.from(txId).toString('hex'))

        // Which previous output index from txid hash has to be spent
        const vout = new Uint8Array(new ArrayBuffer(4))
        vout.set([n,0x00,0x00,0x00])
        //console.log("[VOUT]: ",Buffer.from(vout).toString('hex'))
      
        const scriptSig2 = new Uint8Array(redeem.length+1)

        scriptSig2[0] = redeem.length
        scriptSig2.set(redeem,1)
        
        if(this.debugger){console.log("[SCRIPT_SIG]: ",Buffer.from(scriptSig2).toString('hex'))}

        const sequence = new Uint8Array(new ArrayBuffer(4))
        sequence.set([0xFF,0xFF,0xFF,0xFD])
        //console.log("[SEQUENCE]: ",Buffer.from(sequence).toString('hex'))

        const input = {
            hash: utxo,
            vout: Buffer.from(vout).toString('hex'),
            script_size: this.segwit ? Buffer.from([0x00]).toString('hex') : this.type === "p2sh" || this.type === "p2wsh" ? Buffer.from([0x00]).toString('hex') : scriptSig2.length,
            unlock_scriptsig: this.segwit ? this.type === "p2sh" || this.type === "p2wsh" ? Buffer.from("").toString('hex') : Buffer.from([0x00]).toString('hex') : Buffer.from(scriptSig2).toString('hex'),
            pubkey: script,
            sequence: Buffer.from(this.sequence).toString('hex'),
            witness: ""
        }
        this.debugger && console.log("[INPUT]: ", input)

        const inputRaw = Buffer.concat([
            //Buffer.from(inputCount),
            Buffer.from(txId),
            Buffer.from(vout),
            this.segwit? Buffer.from([0x00]) : scriptSig2,
            Buffer.from(this.sequence)
        ])
        
        //console.log(`[${inputRaw.length}]`,inputRaw.toString('hex'))
        
        this.vins.push(inputRaw)

        this.unsignedtx.vins.push(Buffer.concat([Buffer.from(txId),Buffer.from(vout),Buffer.from([0x00]),Buffer.from(this.sequence)]))
        this.txtosign.vins.push(Buffer.concat([Buffer.from(txId),Buffer.from(vout),scriptSig2,Buffer.from(this.sequence)]))
        this.signedtx.vins.push(Buffer.concat([Buffer.from(txId),Buffer.from(vout)]))

        this.inputCount = this.inputCount + 1

        this.unsignedtx.inputcount = new Uint8Array([this.inputCount,0x00,0x00,0x00])
        this.txtosign.inputcount = new Uint8Array([this.inputCount,0x00,0x00,0x00])

        return this
    }

    /**
     * Create an output given an amount in satoshi and address receiver
     * @param {*} valueIn 
     * @param {*} address 
     * @returns this
     */
    createOutput(valueIn=0,address="") {
        /**
         * Outputs Count
         * [value, script_pubkey]
         */
        let addressDecoded
        let outputScript
        let scriptDetected = ""

        if (address.startsWith('1') || address.startsWith('m')) {
            this.debugger && console.log("***P2PKH OUTPUT***")
            addressDecoded = base58.decode(address)
        }
        if (address.startsWith('3') || address.startsWith('2')) {
            this.debugger && console.log("***P2SH OUTPUT***")
            addressDecoded = base58.decode(address)
        }
        if (address.startsWith('bc') && address.length === 42 || address.startsWith('tb') && address.length === 42) {
            this.debugger && console.log("***P2WPKH OUTPUT***",address.length)
            scriptDetected = "p2wpkh"
            addressDecoded = bech32.decode(address,42).words
        }
        if (address.startsWith('bc') && address.length > 42 || address.startsWith('tb') && address.length > 42) {
            this.debugger && console.log("***P2WSH OUTPUT***",address.length)
            scriptDetected = "p2wsh"
            addressDecoded = bech32.decode(address,62).words
        }

        const decodedHash = new Uint8Array(Buffer.from(addressDecoded, 'hex')).slice(1,addressDecoded.length - 4)
        
        switch(addressDecoded[0]) {
            case 0x00:
                if(this.debugger){console.log("[SCRIPT-DETECTION]: ","0x00","P2PKH","MAINNET")}
                outputScript = Buffer.concat([
                    new Uint8Array([OP_DUP,OP_HASH160,0x14]),
                    decodedHash,
                    new Uint8Array([OP_EQUALVERIFY,OP_CHECKSIG]),
                ])
                if (scriptDetected === "p2wpkh") {
                    const decoded = Buffer.from(this.convertbits(addressDecoded.slice(1),5,8,false))
                    outputScript = Buffer.concat([Uint8Array.from([OP_0]), Uint8Array.from([0x14]), decoded])
                }
                if (scriptDetected === "p2wsh") {
                    const decoded = Buffer.from(this.convertbits(addressDecoded.slice(1),5,8,false))
                    outputScript = Buffer.concat([Uint8Array.from([OP_0]), Uint8Array.from([0x20]), decoded])
                }
                break
            case 0x05:
                if(this.debugger){console.log("[SCRIPT-DETECTION]: ","0x05","P2SH","MAINNET")}
                outputScript = Buffer.concat([
                    new Uint8Array([OP_HASH160,0x14]),
                    decodedHash,
                    new Uint8Array([OP_EQUAL]),
                ])
                break
            case 0x6f:
                if(this.debugger){console.log("[SCRIPT-DETECTION]: ","0x6f","P2PKH","TESTNET")}
                outputScript = Buffer.concat([
                    new Uint8Array([OP_DUP,OP_HASH160,0x14]),
                    decodedHash,
                    new Uint8Array([OP_EQUALVERIFY,OP_CHECKSIG]),
                ])
                break
            case 0xc4:
                if(this.debugger){console.log("[SCRIPT-DETECTION]: ","0xc4","P2SH","TESTNET")}
                outputScript = Buffer.concat([
                    new Uint8Array([OP_HASH160,0x14]),
                    decodedHash,
                    new Uint8Array([OP_EQUAL])
                ])
                break
            default:
                console.log("[SCRIPT DETECTION] error")
        }

        this.debugger && console.log("[OUTPUT_SCRIPT]: ", Buffer.from(outputScript).toString('hex'))

        const output = {
            script_size: outputScript.length,
            lock_scriptpubkey: Buffer.from(outputScript).toString('hex'),
            value: valueIn,
            address: address
        }

        this.debugger && console.log("[OUTPUT]: ", output)

        const outputRaw = Buffer.concat([
            Buffer.from(this.longToByteArray(valueIn)),
            Buffer.from(new Uint8Array([outputScript.length])),
            outputScript
        ])
        //console.log(`[${outputRaw.length}]`,outputRaw.toString('hex'))

        this.vouts.push(outputRaw)
        this.amount = this.longToByteArray(valueIn)
        this.outputScript = Uint8Array.from([...this.amount,outputScript.length,...outputScript])

        this.unsignedtx.vouts.push(Buffer.concat([Buffer.from(this.longToByteArray(valueIn)),Buffer.from(new Uint8Array([outputScript.length])),outputScript]))
        this.txtosign.vouts.push(Buffer.concat([Buffer.from(this.longToByteArray(valueIn)),Buffer.from(new Uint8Array([outputScript.length])),outputScript]))
        this.signedtx.vouts.push(Buffer.concat([Buffer.from(this.longToByteArray(valueIn)),Buffer.from(new Uint8Array([outputScript.length])),outputScript]))

        this.outputCount = this.outputCount + 1

        this.unsignedtx.outputcount = new Uint8Array([this.outputCount,0x00,0x00,0x00])
        this.txtosign.outputcount = new Uint8Array([this.outputCount,0x00,0x00,0x00])
        this.signedtx.outputcount = new Uint8Array([this.outputCount,0x00,0x00,0x00])

        return this
    }

    /**
     * Add witness program
     * @param {*} data 
     * @returns 
     */
    addWitness(data) {
        
        let witness
        // if data < 75 |--> 0x??
        if (Buffer.from(data,'hex').length <= 75) {
            witness = Buffer.concat([Uint8Array.from([data.length]),Buffer.from(data,'hex')])
            this.debugger && console.log("[WIT < 75]",data.length,Buffer.from(data,'hex').length)            
        }
        // if data > 75 < 253 |--> 0x4c
        if (Buffer.from(data,'hex').length > 75 && Buffer.from(data,'hex').length <= 253 ) {
            witness = Buffer.concat([Uint8Array.from([Buffer.from(data,'hex').length]),Buffer.from(data,'hex')])
            this.debugger && console.log("[75 < WIT < 253]",data.length,Buffer.from(data,'hex').length.toString(16))
        }
        // if data > 253 |--> 0xfd 0xff 0x01
        if (Buffer.from(data,'hex').length >= 253) {
            //...Buffer.from(Buffer.from(array,'hex').length.toString(16).padStart(4, '0'),'hex').reverse()
            witness = Buffer.concat([
                Uint8Array.from([0xfd, ...Buffer.from(Buffer.from(data,'hex').length.toString(16).padStart(4, '0'),'hex').reverse()]),
                Buffer.from(data,'hex')
            ])
            this.debugger && console.log("[WIT > 253]",data.length,Buffer.from(data,'hex').length.toString(16))
        }
        
        //const witness = Buffer.concat([Uint8Array.from([data.length]),Buffer.from(data,'hex')])
        //const witness = Buffer.concat([data.length > 75 ? Uint8Array.from([0x4c,Buffer.from(data,'hex').length.toString(16)]):Uint8Array.from([data.length]),Buffer.from(data,'hex')])

        this.debugger && console.log("[WITNESS]: ", Buffer.from(witness).toString('hex'))

        this.witnessCount = this.witnessCount + 1

        this.witness.push(...witness)

        //console.log("[FINAL]",Buffer.concat([new Uint8Array([this.witnessCount]), new Uint8Array([witness.length]),witness]))

        return this
    }

    /**
     * Function used to add and DER encode various signatures with relative public keys
     * @param {*} sig Signature
     * @param {*} pubkey Pubkey
     */
    addSignatures(sig,pubkey) { // input parameter // TODO: FIX tx[0]:[{sig,pubkey}]
        this.signatures.push({sig,pubkey})
    }

    /**
     * Function used to DER encode a signature
     * @param {*} sig Signature to be DER encoded
     */
    encodeSignature(sig) {
        // 0x47 0x30 [total-length] 0x02 [R-length] [R] 0x02 [S-length] [S] [sighash-type] 0x21 [pubkey]
        
        // Extract R and S -> Check Low S -> Check First Byte if > 0x80
        const order = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141") //Uint8Array.from(Buffer.from("7FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF5D576E7357A4501DDFE92F46681B20A0",'hex'))
        const sub = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141") //Uint8Array.from(Buffer.from("FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",'hex')) // - S
        
        const R = Uint8Array.from(Buffer.from(sig.slice(0,32)))
        const S = Uint8Array.from(Buffer.from(sig.slice(32,64)))
        const sToCheck = BigInt(`0x${Buffer.from(S).toString('hex')}`)

        const Rbyte = R[0]
        const Sbyte = S[0]

        // High or Low S
        const check = sToCheck >= (order / BigInt(2)) //S >= order/2

        const sTweaked = check ? Buffer.from((order - sToCheck).toString(16),'hex') : 0x00
        //console.log("[S-Tweaked]", sTweaked)
        const STweakedbyte = sTweaked[0]

        //const x = BigInt("115792089237316195423570985008687907852837564279074904382605163141518161494337")
        //const y = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141")
        //const test = Buffer.from(S).toString('hex')
        //const z = BigInt("0x3f6a96f81cfad1c97bec2a5921a08e964f85cf5f1207450bd7956c72aadbf448")
        
        //console.log("??? ", Buffer.from(sig.slice(0,32)))
        //console.log(". . . ", sub)
        //console.log("= = = ", BigInt(`0x${test}`))
        //console.log("* * * ", Buffer.from((sub - sToCheck).toString(16),'hex')) //Buffer.from((sub - sToCheck).toString(16))

        const checkR = Rbyte > 0x7f
        const checkS = check? STweakedbyte > 0x7f: Sbyte > 0x7f
        //const checkSTweaked = STweakedbyte > 0x80
        //const lowS = checkSTweaked ? new Uint8Array([0x00,...sTweaked]) : sTweaked

        //const len = new Uint8Array([0x47])
        const der = new Uint8Array([0x30])
        
        const a = new Uint8Array([0x02])
        const rlen = checkR ? new Uint8Array([R.length + 1]) : new Uint8Array([R.length])
        const r = checkR ? new Uint8Array([0x00,...R]) : R
        const b = new Uint8Array([0x02])
        const slen = check ? checkS ? new Uint8Array([sTweaked.length + 1]) : new Uint8Array([sTweaked.length]) : checkS ? new Uint8Array([S.length + 1]) : new Uint8Array([S.length]) //checkS ? new Uint8Array([S.length + 1]) : new Uint8Array([S.length])
        const s = check ? checkS ? new Uint8Array([0x00,...sTweaked]) : sTweaked : checkS ? new Uint8Array([0x00,...S]) : S //checkS ? new Uint8Array([0x00,...S]) : S // lowS ???
        const sighash = this.sighash //new Uint8Array([0x01])

        const lentot = new Uint8Array([a.length + rlen.length + r.length + b.length + slen.length + s.length])

        const encoded = Buffer.concat([der, lentot, a, rlen, r, b, slen, s, sighash])

        if (this.debugger) {
            console.log("{R}",Rbyte,"{S}",Sbyte,0x7f)
            console.log("[Signature Check]: ", 
                "[S > N/2]:",check, 
                "[R > 0x80]:", checkR, 
                "[S > 0x80]", checkS, 
                Buffer.from(R).toString('hex'), 
                Buffer.from(S).toString('hex'), 
                sTweaked,         
                sighash   
            )
            console.log("[DER Canonical]: ", Buffer.from(encoded).toString('hex'))
        }
        return encoded
    }

    /**
     * Internal function used to create scripts
     * @param {*} type Script format `p2pk|p2pkh|p2sh`
     * @param {*} data
     * @returns 
     */
    scripter(type="p2sh",data) {
        let dataIn
        let dataIn2
        let script
        let len

        const op0 = new Uint8Array([0x00])
        const op1 = new Uint8Array([0x01])
        const opDup = new Uint8Array([OP_DUP])
        const opChecksig = new Uint8Array([OP_CHECKSIG])
        const opHash160 = new Uint8Array([OP_HASH160])
        const opEqual = new Uint8Array([OP_EQUAL])
        const opEqualVerify = new Uint8Array([OP_EQUALVERIFY])

        switch(type) {
            case "p2pk":
                // P2PK | <33 or 65 bytes pubkey> OP_CHECKSIG
                dataIn = Buffer.from(data,'hex')
                dataIn2 = new Uint8Array(Buffer.from(data,'hex').length)
                dataIn2.set(Buffer.from(data,'hex'))

                script = Buffer.concat([dataIn2, opChecksig])
                this.debugger && console.log("\n[SCRIPTER:p2pk]: ",Buffer.from(script).toString('hex'),"\n")
                return script
            case "p2ms":
                // P2MS | <M <pubkey1> <pubkey2> N> OP_CHECKMULTISIG
                return
            case "p2pkh":
                // P2PKH | OP_DUP OP_HASH160 <20 bytes pubkey hash> OP_EQUALVERIFY OP_CHECKSIG
                dataIn = ripemd160.create().update((sha256(Buffer.from(data,'hex')))).digest()
                len = new Uint8Array([dataIn.length])
                script = Buffer.concat([opDup, opHash160, len, dataIn, opEqualVerify ,opChecksig])
                this.debugger && console.log("\n[SCRIPTER:p2pkh]: ",Buffer.from(script).toString('hex'),"\n")
                return script
            case "p2sh":
                // P2SH | OP_HASH160 <20 bytes pubkey hash> OP_EQUAL
                dataIn = ripemd160.create().update((sha256(Buffer.from(data,'hex')))).digest()
                len = new Uint8Array([dataIn.length])
                script = Buffer.concat([opHash160, len, dataIn, opEqual])
                this.debugger && console.log("\n[SCRIPTER:p2sh]: ",Buffer.from(script).toString('hex'),"\n")
                return script
            case "p2wpkh":
                // P2WPKH | 0 <20 bytes pubkey hash>
                dataIn = ripemd160.create().update((sha256(Buffer.from(data,'hex')))).digest()
                len = new Uint8Array([dataIn.length])                
                script = Buffer.concat([op0, len, dataIn])
                this.debugger && console.log("\n[SCRIPTER:p2wpkh]: ",Buffer.from(script).toString('hex'),"\n")
                return script
            case "p2wsh":
                // P2WSH | 0 <32 bytes script hash>
                dataIn = sha256(Buffer.from(data,'hex'))
                len = new Uint8Array([dataIn.length])
                script = Buffer.concat([op0, len, dataIn])
                this.debugger && console.log("\n[SCRIPTER:p2wsh]: ",Buffer.from(script).toString('hex'),"\n")
                return script
            case "p2tr":
                // P2TR | 1 <32 bytes script hash>
                dataIn = sha256(Buffer.from(data,'hex'))
                len = new Uint8Array([dataIn.length])
                script = Buffer.concat([op1, len, dataIn])
                return
            default:
                console.log("No script selected")
        }

        //return Buffer.from(script)
    }

    /**
     * Serialized Unsigned TX
     */
    serializeUnsigned() {
        const tmp = []
        
        tmp.push(...this.unsignedtx.version)
        //if (this.segwit) {tmp.push(...new Uint8Array([0x00,this.flag]))}
        tmp.push(this.inputCount) //new Uint8Array([0x00])
        this.unsignedtx.vins.map(buf=>tmp.push(...buf))
        tmp.push(this.outputCount)
        this.unsignedtx.vouts.map(buf=>tmp.push(...buf))
        //if (this.segwit) {tmp.push(this.witnessCount)}
        tmp.push(...this.unsignedtx.locktime)

        this.debugger && console.log("[TX Unsigned]: ",Buffer.from(tmp).toString('hex'))
        return tmp
    }

    /**
     * Serialize to Sign - include ScriptPubKey from previous utxo for each input ScriptSig's
     * Pass N = Input to Sign , IF SegWit UTXO amount & ScriptCode
     */
    serializeToSign(index=0,utxoAmount=0,scriptCode="") {
        const tmp = []
        
        tmp.push(...this.txtosign.version)
        tmp.push(this.inputCount)
        this.txtosign.vins.map((buf,i)=>{
            if (index===i) {
                tmp.push(...buf)
            }
        })
        tmp.push(this.outputCount)
        this.txtosign.vouts.map(buf=>tmp.push(...buf))
        tmp.push(...this.txtosign.locktime)
        tmp.push(...new Uint8Array([this.sighash,0x00,0x00,0x00]))

        // SEGWIT
        if (this.segwit) {
            // preimage = version + hash256(inputs) + hash256(sequences) + input + scriptcode + amount + sequence + hash256(outputs) + locktime
            // wtxid = hash256 [nVersion][marker][flag][txins][txouts][witness][nLockTime]
            let ram = []
            let seq = []
            let out = []
            let outs = []
            const seg = []

            // versions
            seg.push(...this.txtosign.version)

            // inputs
            this.txtosign.vins.map(buf=>{
                ram.push(...buf.slice(0,32))
                ram.push(...buf.slice(32,36))
                seq.push(...buf.slice(buf.length-4,buf.length))
            })
            const inputs = sha256(sha256(Buffer.from(ram)))
            seg.push(...inputs)
            
            // sequences
            const sequences = sha256(sha256(Buffer.from(seq)))
            seg.push(...sequences)

            // inputs outpoint txid + vout + scriptcode + utxo amount + sequence
            this.txtosign.vins.map((buf,i)=>{
                if (index===i) {
                    out.push(...buf.slice(0,32))
                    out.push(...buf.slice(32,36))
                    out.push(...Buffer.from(scriptCode,'hex'))
                    out.push(...this.longToByteArray(utxoAmount))
                    out.push(...buf.slice(buf.length-4,buf.length))
                }
            })
            seg.push(...out)

            // outputs
            this.txtosign.vouts.map(buf=>{
                outs.push(...buf)
            })
            const outputs = sha256(sha256(Buffer.from(outs)))
            seg.push(...outputs)

            // locktime
            seg.push(...this.txtosign.locktime)

            // sighash
            seg.push(...new Uint8Array([this.sighash,0x00,0x00,0x00]))

            this.debugger && console.log("[TX SEGWIT to SIGN]: ", Buffer.from(seg).toString('hex'))
            return seg
        }

        this.debugger && console.log("[TX To SIGN]: ",Buffer.from(tmp).toString('hex'))
        return tmp
    }

    /**
     * Serialize to Broadcast - include ScriptSig for each input
     * Pass Redeem Script
     */
    serializeSigned(redeemScript="") {
        const tmp = []
        tmp.push(...this.signedtx.version)
        tmp.push(this.inputCount)
        this.signedtx.vins.map((buf,i)=>{ // TODO FIX: map input tx signatures
            const script = [...this.encodeHex(this.encodeSignature(this.signatures[i].sig)),...this.encodeHex(this.signatures[i].pubkey)]
            tmp.push(...buf,...this.encodeHex(script),...this.sequence)
        })
        tmp.push(this.outputCount)
        this.signedtx.vouts.map(buf=>tmp.push(...buf))
        tmp.push(...this.signedtx.locktime)

        // SEGWIT
        if (this.segwit) {
            const seg = []
            let ram = []

            // versions
            seg.push(...this.signedtx.version)

            // FLAG
            seg.push(...new Uint8Array([0x00,this.flag]))

            // INPUT COUNT
            seg.push(this.inputCount)

            // INPUTS
            this.signedtx.vins.map((buf,i)=>{
                // P2SH-P2WPKH | P2SH-P2WSH | P2WPKH | P2WSH
                const redeemcode = Buffer.from(redeemScript,'hex')
                seg.push(...buf,...this.encodeHex(redeemcode),...this.sequence)
                ram.push(...buf,...this.encodeHex(redeemcode),...this.sequence)
            })

            // OUTPUT COUNT 
            seg.push(this.outputCount)

            // OUTPUTS
            this.signedtx.vouts.map(buf=>seg.push(...buf))

            // WITNESS
            seg.push(this.witnessCount)
            seg.push(...this.witness)

            // LOCKTIME
            seg.push(...this.signedtx.locktime)

            this.debugger && console.log("[TX SEGWIT SIGNED]: ", Buffer.from(seg).toString('hex'))

            const noseg = Uint8Array.from([
                ...this.version,
                this.inputCount,
                ...ram,
                this.outputCount,
                ...Buffer.from(...this.signedtx.vouts),
                ...this.locktime
            ])
            
            this.txid = sha256(sha256(noseg)).reverse()
            this.wtxid = sha256(sha256(Buffer.from(seg))).reverse()

            //console.log("[WTXID]: ", Buffer.from(this.wtxid).toString('hex'))
            //console.log("[SegWit-TXID]: ", Buffer.from(sha256(sha256(this.wtxid)).reverse()).toString('hex'))

            this.size = seg.length
            this.weight = (noseg.length * 4) + (2 + this.witness.length)
            this.vsize = noseg.length + ((2 + this.witness.length)/4)

            this.debugger && console.log("[Size]: ", this.size)
            this.debugger && console.log("[vSize]: ", this.vsize)
            this.debugger && console.log("[Sats/Byte]: ", (this.fee/this.size).toFixed(2))
            this.debugger && console.log("[Sats/vByte]: ", (this.fee/this.vsize).toFixed(2))
            return seg
        }

        this.debugger && console.log("[TX Signed]: ",Buffer.from(tmp).toString('hex'))
        this.txid = sha256(sha256(Buffer.from(tmp)))
        this.size = tmp.length
        this.weight = (tmp.length * 4) + (2 + this.witness.length)
        this.vsize = tmp.length + ((2 + this.witness.length)/4)

        this.debugger && console.log("[Size]: ", this.size)
        this.debugger && console.log("[vSize]: ", this.vsize)
        this.debugger && console.log("[Sats/Byte]: ", (this.fee/this.size).toFixed(2))
        this.debugger && console.log("[Sats/vByte]: ", (this.fee/this.vsize).toFixed(2))

        return tmp
    }

    encodeHex(array) {
        const hex = [array.length,...array]

        // toString(16).padStart(4, '0')
        let hex2
        // if data < 75 bytes |--> data.len
        if (Buffer.from(array,'hex').length <= 75) {
            hex2 = Buffer.concat([
                Uint8Array.from([array.length]),
                Buffer.from(array,'hex')
            ])
            if (this.debugger) {
                console.log("[DATA < 75]",array.length,Buffer.from(array,'hex').length.toString(16).padStart(2, '0'))
            }
        }
        // if data > 75 < 253 |--> 0xfc
        if (Buffer.from(array,'hex').length > 75 && Buffer.from(array,'hex').length < 253 ) {
            hex2 = Buffer.concat([
                //Uint8Array.from([ 0xfc, Buffer.from(array,'hex').length.toString(16)]),
                Buffer.from(Buffer.from(array,'hex').length.toString(16),'hex'),
                Buffer.from(array,'hex')
            ])
            if (this.debugger) {
                console.log("[75 < DATA < 253]",array.length,Buffer.from(array,'hex').length.toString(16))
            }            
        }
        // if data > 253 |--> 0xfd 0x0100 (256bytes) || 0x0802 (520bytes)
        if (Buffer.from(array,'hex').length >= 253) {
            hex2 = Buffer.concat([
                Uint8Array.from([0xfd, ...Buffer.from(Buffer.from(array,'hex').length.toString(16).padStart(4, '0'),'hex').reverse()]),
                Buffer.from(array,'hex')
            ])
            if (this.debugger) {
                console.log("[DATA > 253]", array.length,Buffer.from(array,'hex').length.toString(16).padStart(4, '0'))
            }            
        }

        if (Buffer.from(array,'hex').length > 520) {
            this.debugger && console.log("[***MAX_LENGTH***]")
            hex2 = []
        }
        
        //this.log("encodeHEX", Buffer.from(hex).toString('hex'))
        return hex2
    }

    decodeHex(array) {
        const length = array.length
        let ptr = 0
        let buf = []

        while (ptr < length) {
            const op = array[ptr]
            if (ptr>length) {
                //this.log("PTR","END")
                return
            }
            if (op === 0x00) {
                //this.log("PTR","0x00")
                return
            }
            if (op > 0 || op < 0x4c) {
                const tmp = array.slice(ptr+1,op+1)
                //this.log("TMP",tmp)
                buf.push(...tmp)
                ptr += op +1
            }
        }
        //this.log("decodeHEX", Buffer.from(buf).toString('hex'))
        return buf
    }

    setFee(fee=1000) {
        this.fee = fee
    }

    longToByteArray(/*long*/long) {
        // we want to represent the input as a 8-bytes array
        var byteArray = [0, 0, 0, 0, 0, 0, 0, 0];
    
        for ( var index = 0; index < byteArray.length; index ++ ) {
            var byte = long & 0xff;
            byteArray [ index ] = byte;
            long = (long - byte) / 256 ;
        }
    
        return byteArray;
    }

    convertbits(data, frombits, tobits, pad) {
        var acc = 0;
        var bits = 0;
        var ret = [];
        var maxv = (1 << tobits) - 1;
        for (var p = 0; p < data.length; ++p) {
            var value = data[p];
            if (value < 0 || (value >> frombits) !== 0) {
            return null;
            }
            acc = (acc << frombits) | value;
            bits += frombits;
            while (bits >= tobits) {
            bits -= tobits;
            ret.push((acc >> bits) & maxv);
            }
        }
        if (pad) {
            if (bits > 0) {
            ret.push((acc << (tobits - bits)) & maxv);
            }
        } else if (bits >= frombits || ((acc << (tobits - bits)) & maxv)) {
            return null;
        }
        return ret;
    }

    debug() {
        const tx = this
        console.log("[hash]:", Buffer.from(this.txid).toString('hex'))
        console.log("[version]:",Buffer.from(tx.version).toString('hex'))
        console.log("[segwit]:", Buffer.from(tx.flag).toString('hex'))
        console.log("[vins]:",tx.vins.map(item=>{return Buffer.from(item).toString('hex')}))
        console.log("[inputs]:",tx.inputs.map(item=>{return Buffer.from(item).toString('hex')})) // SegWit
        console.log("[vouts]:",tx.vouts.map(item=>Buffer.from(item).toString('hex')))
        console.log("[locktime]:",Buffer.from(tx.locktime).toString('hex'))
        console.log("[signature]:", Buffer.from(this.signature).toString('hex'))
        console.log("[sighash]:", Buffer.from(this.sighash).toString('hex'))
        console.log("[scriptsig]:", Buffer.from(this.scriptSig).toString('hex'))
        console.log("[size]:", this.size)
        console.log("[weight]:", this.weight)
        console.log("[vsize]:", this.vsize)
    }

    log(name, data) {
        console.log(`[${name}]: `, data)
    }
}

export class PSBT {
    /**
     * <psbt> := <magic> <global-map> <input-map>* <output-map>*
     * <magic> := 0x70 0x73 0x62 0x74 0xFF
     * <global-map> := <keypair>* 0x00
     * <input-map> := <keypair>* 0x00
     * <output-map> := <keypair>* 0x00
     * <keypair> := <key> <value>
     * <key> := <keylen> <keytype> <keydata>
     * <value> := <valuelen> <valuedata>
     * @param {*} version 
     */
    constructor(version=2) {
        this.debugger = false
        // MAGIC BYTES
        this.magic = Uint8Array.from([0x70,0x73,0x62,0x74,0xFF])
        // Unsigned Transaction 0x00 (req_v0)
        this.unsignedtx = [0x01,0x00]
        // Extended Public Keys 0x01
        this.xpub = [0x01]
        // Version Unsigned TX
        this.txversion = [0x01,0x02,0x04] //0x02 (req_v2) (no_v0)
        // Fallback Locktime 0x03 (req_v2) (no_v0)
        this.locktime = [0x01,0x03,0x04]
        // Input Count 0x04 (req_v2) (no_v0)
        this.inCount = [0x01,0x04,0x01]
        //this.inputCount = [0x01,0x04]
        this.icount = 0
        // Output Count 0x05 (req_v2) (no_v0)
        this.outCount = [0x01,0x05,0x01]
        //this.outputCount = [0x05]
        this.ocount = 0
        // Transaction Modifiable flag 0x06 (no_v0)
        this.modFlag = [0x01,0x06,0x01]
        // PSBT Version Number 0xfb
        this.version = version
        this.psbtversion = [0x01,0xfb,0x04, ...Uint8Array.from([version,0x00,0x00,0x00])] //0xfb
        // Proprietary Use Type 0xfc

        this.witnessCount = 0
        this.witnesses = []

        this.unsignedpsbt = {
            magic : [...this.magic],
            globalMap: [],
            inputMap: [],
            outputMap: []
        }

        this.hex = ""
        this.base64 = ""
    }

    importFromHex(hex) {
        this.hex = hex
        const buffer = Buffer.from(hex)
        //console.log("[ImportFromHex]: ", Buffer.from(buffer).toString('hex'))
    }

    importFromBase64(base64) {
        this.base64 = base64
        const hex = Buffer.from(base64,'base64')
        this.importFromHex(hex)
        //console.log("[ImportBase64]: ", this.base64)
    }

    addGlobalMap(unsignedTx, version, keyChain) {
        /**
         * <keypair> := <key> <value>
         * <key> := <keylen> <keytype> <keydata>
         * <value> := <valuelen> <valuedata>
         */
        const tmp = []
        const ext = []
        const end = 0x00

        // Unsigned Tx <0x00><bytes tx>
        //this.unsignedtx.push(...Uint8Array.from([unsignedTx.length]),...unsignedTx)
        this.unsignedtx.push(...this.encodeHex(unsignedTx))
        tmp.push(...this.unsignedtx)
        //console.info("[Global-UnsignedTX]: ",Buffer.from(this.unsignedtx).toString('hex'))

        // Extended Public Keys <0x01><4byte fgpr><4byte le path>*
        keyChain.length > 0 ? keyChain.map(el=>{ext.push(...this.addXpub(el.xpub,el.path,el.fgpr))}) : null
        keyChain.length > 0 ? tmp.push(...ext) : null
        
        // TX Version <0x02><4byte le version>
        if (this.version !== 0) {
            this.txversion.push(...new Uint8Array([version,0x00,0x00,0x00]))
            tmp.push(...this.txversion)
            //console.info("[Global-TX-version]: ",Buffer.from(this.txversion).toString('hex'))
        }

        // Fallback Locktime <0x03><4byte le locktime>
        if (this.version !== 0) {
            this.locktime.push(...new Uint8Array([0xff,0xff,0xff,0xfd]))
            tmp.push(...this.locktime)
            //console.info("[Global-locktime]: ",Buffer.from(this.locktime).toString('hex'))
        }        

        // Input Count <0x04><uint>
        if (this.version !== 0) {
            this.inCount.push(...new Uint8Array([this.icount]))
            tmp.push(...this.inCount)
            //console.info("[Global-Input-Count]: ",Buffer.from(this.inCount).toString('hex'))
        }        

        // Output Count <0x05><uint>
        if (this.version !== 0) {
            this.outCount.push(...Uint8Array.from([this.ocount]))
            tmp.push(...this.outCount)
            //console.info("[Global-Output-Count]: ",Buffer.from(this.outCount).toString('hex'))
        }        

        // Transaction Modifiable Flag <0x06><1byte>
        if (this.version !== 0) {
            this.modFlag.push(0x01)
            tmp.push(...this.modFlag)
            //console.info("[Global-Mod-Flag]: ",Buffer.from(this.modFlag).toString('hex'))
        }        

        // PSBT Version number <0xfb><4byte le version>
        if (this.version !== 0) {
            tmp.push(...this.psbtversion) // TODO
            //console.info("[GlobalPSBT-Version]: ",Buffer.from(this.psbtversion).toString('hex'))
        }

        // Separator
        tmp.push(end)

        this.unsignedpsbt.globalMap.push(...tmp)

        //console.log("[GLOBAL-MAP]: ", Buffer.from(tmp).toString('hex'))
    }

    addInput(segwit=false,utxo="",txid="",vout=0,bip32Chain=[], partialSigs=[], redeemScript="", witnessScript="",extra="") {
        /**
         * If Segwit add WITNESS-UTXO amount+out else NON WITNESS-UTXO tx
         */
        const tmp = []
        const end = 0x00

        // Non WitnessUtxo 0x00
        // WitnessUtxo 0x01
        if (!segwit) {
            const nonWitUtxo = [0x01,0x00]
            //nonWitUtxo.push(...Uint8Array.from([Buffer.from(utxo,'hex').length]),...Buffer.from(utxo,'hex'))
            nonWitUtxo.push(...this.encodeHex(Buffer.from(utxo,'hex')))
            tmp.push(...nonWitUtxo)
        } else {
            if (extra.length > 0 ) {
                const nonWitUtxo = [0x01,0x00]
                //nonWitUtxo.push(...Uint8Array.from([Buffer.from(utxo,'hex').length]),...Buffer.from(utxo,'hex'))
                nonWitUtxo.push(...this.encodeHex(Buffer.from(extra,'hex')))
                tmp.push(...nonWitUtxo)
            }
            const witUtxo = [0x01,0x01]
            //witUtxo.push(...Uint8Array.from([Buffer.from(utxo,'hex').length]),...Buffer.from(utxo,'hex'))
            witUtxo.push(...this.encodeHex(Buffer.from(utxo,'hex')))
            tmp.push(...witUtxo)
        }
        
        // Partial Signature 0x02
        if (partialSigs.length > 0) {
            partialSigs.map(el=>{
                tmp.push(...this.addPartialSig(el.pub,el.sig))
            })
        }
        // Sighash Type 0x03
        // Redeem Script 0x04
        if (redeemScript.length > 0) {
            //this.addRedeemScript("input",redeemScript)
            tmp.push(...this.addRedeemScript("input",redeemScript))
            //const keytype = Uint8Array.from([0x01,0x04])
            //tmp.push(...keytype)
            //tmp.push(...this.encodeHex(Buffer.from(redeemScript,'hex')))
        }
        // Witness Script 0x05
        if (witnessScript.length > 0) {
            //this.addWitnessScript("input",witnessScript)
            tmp.push(...this.addWitnessScript("input",witnessScript))
            //const keytype = Uint8Array.from([0x01,0x05])
            //tmp.push(...keytype)
            //tmp.push(...this.encodeHex(Buffer.from(witnessScript,'hex')))
            this.witnessCount++
        }

        // Bip32 Derivation Path 0x06
        if (bip32Chain.length > 0) {
            bip32Chain.map(el=>{
                tmp.push(...this.addBip32("input",el.pub,el.fgpr,el.path))
            })
        }
        // Finalized ScriptSig 0x07
        // Finalized ScriptWitness 0x08
        // POR Commit 0x09
        // Ripemd160 PreImage 0x0a
        // Sha256 PreImage 0x0b
        // Hash160 PreImage 0x0c
        // Hash256 PreImage 0x0d
        // Previous TXID 0x0e (req_v2) (no_v0)
        if (this.version !== 0) {
            const prevTxid = Buffer.from(txid,'hex')
            const prevtxid = Uint8Array.from([0x01,0x0e,prevTxid.length,...prevTxid])
            tmp.push(...prevtxid)
            //console.log("[INPUT-PREV-TXID]: ", Buffer.from(prevtxid).toString('hex'))
        }
        // Spent Output Index 0x0f (req_v2) (no_v0)
        if (this.version !== 0) {
            const prevOut = Uint8Array.from([0x01,0x0f,0x04,...Uint8Array.from([vout,0x00,0x00,0x00])])
            tmp.push(...prevOut)
            //console.log("[INPUT-PREV-VOUT]: ", Buffer.from(prevOut).toString('hex'))
        }
        // Sequence Number 0x10 (no_v0)
        // Locktime TimeBased 0x11 (no_v0)
        // Locktime HeightBased 0x12 (no_v0)
        // Taproot Key Spend Signature 0x13
        // Taproot Script Spend Signature 0x14
        // Taproot Leaf Script 0x15
        // Taproot Key Bip32 Derivation Path 0x16
        // Taproot Internal Key 0x17
        // Taproot Merkle Root 0x18
        // MuSig2 Public Keys 0x1a
        // MuSig2 Public Nonce 0x1b
        // MuSig2 Partial Signature 0x1c
        // Proprietary Use Type 0xfc
        
        // Separator
        tmp.push(end)

        this.unsignedpsbt.inputMap.push(...tmp)

        //console.log("[INPUT-MAP]: ", Buffer.from(tmp).toString('hex'))

        this.icount++
    }

    addOutput(amount=0,outscript="",bip32Chain=[]) {
        const tmp = []
        const end = 0x00

        // Redeem Script 0x00
        // Witness Script 0x01
        // Bip32 Derivation Path 0x02
        if (bip32Chain.length > 0) {
            bip32Chain.map(el=>{
                tmp.push(...this.addBip32("output",el.pub,el.fgpr,el.path))
            })
        }
        // Output Amount 0x03 (req_v2) (no_v0)
        if (this.version !== 0) {
            const outAmount = Uint8Array.from([0x01,0x03,0x08,...this.longToByteArray(amount)])
            tmp.push(...outAmount)
            //console.log("[OUTPUT-AMOUNT]: ", Buffer.from(outAmount).toString('hex'))
        }
        // Output Script 0x04 (req_v2) (no_v0)
        if (this.version !== 0) {
            const outScript = Uint8Array.from([0x01,0x04,outscript.length,...outscript])
            tmp.push(...outScript)
            //console.log("[OUTPUT-SCRIPT]: ", Buffer.from(outScript).toString('hex'))
        }
        // Taproot Internal Key 0x05
        // Taproot Tree 0x06
        // Taproot Key Bip32 Derivation Path 0x07
        // MuSig2 Public Keys 0x08
        // Proprietary Use Type 0xfc

        // Separator
        tmp.push(end)

        this.unsignedpsbt.outputMap.push(...tmp)

        //console.log("[OUTPUT-MAP]: ", Buffer.from(tmp).toString('hex'))

        this.ocount++
    }

    addXpub(data,dpath,mstFgpr) {
        const tmp = []

        const rawxpub = base58.decode(data).slice(0,-4)
        //console.log("RAW-XPUB:: ", rawxpub.length, Buffer.from(rawxpub,'hex').toString('hex'))

        const ver = rawxpub.slice(0,4)
        const depth = rawxpub.slice(4,5)
        const fgpr = Buffer.from(mstFgpr,'hex') //rawxpub.slice(5,9)
        const path = rawxpub.slice(9,13)
        const code = rawxpub.slice(13,13+32)
        const pub = rawxpub.slice(45,45+33)
        //console.log("[Decode Xpub]", Buffer.from(ver).toString('hex'),Buffer.from(depth).toString('hex'),Buffer.from(fgpr).toString('hex'),Buffer.from(path).toString('hex'),Buffer.from(code).toString('hex'),Buffer.from(pub).toString('hex'))
        
        tmp.push(...this.xpub)
        tmp.unshift(rawxpub.length + 1)
        tmp.push(...rawxpub)

        tmp.push(...Uint8Array.from([4 * (dpath.split("/").length)]))

        tmp.push(...fgpr)

        dpath.split("/").map((el,i)=>{
            if (el === "m") {return}
            if (el.includes("h")) {
                const x = el.split("h").join()
                const hex = Uint8Array.from([parseInt(x),0x00,0x00,0x80])//.reverse()
                //console.log(i,Buffer.from(hex).toString('hex'))
                tmp.push(...hex)
            } else {
                const hex = Uint8Array.from([parseInt(el),0x00,0x00,0x00])//.reverse()
                //console.log(i,Buffer.from(hex).toString('hex'))
                tmp.push(...hex)
            }
        })
        
        //console.log("[GLOBAL-XPUB]:: ", parseInt(depth), Buffer.from(this.xpub,'hex').toString('hex'))
        //console.log("[TMP-XPUB]: ",Buffer.from(tmp).toString('hex'))
        //this.unsignedpsbt.globalMap.push(...tmp)
        
        return tmp
    }

    addBip32(mode="input",pub,fgpr,path) {
        const tmp = []

        // 0x06
        if (mode === "input") {
            const keytype = Uint8Array.from([0x22,0x06])
            tmp.push(...keytype)
        }

        // 0x02
        if (mode === "output") {
            const keytype = Uint8Array.from([0x22,0x02])
            tmp.push(...keytype)
        }
        
        tmp.push(...Buffer.from(pub,'hex'))

        tmp.push(...Uint8Array.from([4 * (path.split("/").length)]))

        tmp.push(...Buffer.from(fgpr,'hex'))

        path.split("/").map((item,i)=>{
            if (item === "m") {return}
            if (item.includes("h")) {
                const x = item.split("h").join()
                const hex = Uint8Array.from([parseInt(x),0x00,0x00,0x80])//.reverse()
                //console.log(i,Buffer.from(hex).toString('hex'))
                tmp.push(...hex)
            } else {
                const hex = Uint8Array.from([parseInt(item),0x00,0x00,0x00])//.reverse()
                //console.log(i,Buffer.from(hex).toString('hex'))
                tmp.push(...hex)
            }
        })

        //console.info("[Bip32]: ", mode,Buffer.from(tmp).toString('hex'))
        return tmp
    }

    addPartialSig(pub,sig) {
        // 0x02
        const tmp = []
        tmp.push(...Uint8Array.from([0x22,0x02,...Buffer.from(pub,'hex'),Buffer.from(sig,'hex').length,...Buffer.from(sig,'hex')]))
        
        //console.info("[PartialSig]: ",Buffer.from(tmp).toString('hex'))

        //this.unsignedpsbt.inputMap.push(...tmp)

        return tmp
    }

    addRedeemScript(mode="input",script) {
        const tmp = []
        // 0x04
        if (mode === "input") {
            const keytype = Uint8Array.from([0x01,0x04])
            tmp.push(...keytype)
            tmp.push(...this.encodeHex(Buffer.from(script,'hex')))
            //this.unsignedpsbt.inputMap.push(...tmp)
            return tmp
        }
        // 0x00
        if (mode === "output") {
            const keytype = Uint8Array.from([0x01,0x00])
            tmp.push(...keytype)
            tmp.push(...this.encodeHex(Buffer.from(script,'hex')))
            //this.unsignedpsbt.outputMap.push(...tmp)
            return tmp
        }
        
        this.debugger && console.info("[Redeem-Script]: ",Buffer.from(tmp).toString('hex'))
    }

    addWitnessScript(mode="input",script) {
        const tmp = []
        // 0x05
        if (mode === "input") {
            const keytype = Uint8Array.from([0x01,0x05])
            tmp.push(...keytype)
            tmp.push(...this.encodeHex(Buffer.from(script,'hex')))
            //this.unsignedpsbt.inputMap.push(...tmp)
            this.witnessCount++
            return tmp
        }
        // 0x01
        if (mode === "output") {
            const keytype = Uint8Array.from([0x01,0x01])
            tmp.push(...keytype)
            tmp.push(...this.encodeHex(Buffer.from(script,'hex')))
            //this.unsignedpsbt.outputMap.push(...tmp)
            return tmp
        }
        
        this.debugger && console.info("[Witness-Script]: ",Buffer.from(tmp).toString('hex'))
    }

    addFinalScriptSig(script) {
        const tmp = []
        // 0x07        
        const keytype = Uint8Array.from([0x01,0x07])
        tmp.push(...keytype)
        
        tmp.push(...this.encodeHex(Buffer.from(script,'hex')))
        this.unsignedpsbt.inputMap.push(...tmp)
        
        this.debugger && console.info("[Final-ScriptSig]: ",Buffer.from(tmp).toString('hex'))
    }

    addFinalScriptWitness() {
        const tmp = []
        const ram = []

        // 0x08        
        const keytype = Uint8Array.from([0x01,0x08])
        tmp.push(...keytype)

        const witCount = Uint8Array.from([this.witnessCount])
        
        // WITNESS COUNT
        ram.push(...witCount)

        this.witnesses.map(item=>{
            ram.push(...this.encodeHex(item))
        })

        tmp.push(...this.encodeHex(ram))

        this.unsignedpsbt.inputMap.push(...tmp)
        
        this.debugger && console.info("[Final-ScriptWitness]: ",Buffer.from(tmp).toString('hex'))
    }

    serialize() {
        const tmp = []

        const end = 0x00

        // Magic Bytes
        tmp.push(...this.unsignedpsbt.magic)

        // Global Map + 0x00
        tmp.push(...this.unsignedpsbt.globalMap)
        //this.unsignedpsbt.globalMap.length > 0 ? tmp.push(...this.unsignedpsbt.globalMap) : tmp.push(end)

        // Input Map + 0x00
        //tmp.push(...this.unsignedpsbt.inputMap)
        this.unsignedpsbt.inputMap.length > 0 ? tmp.push(...this.unsignedpsbt.inputMap) : tmp.push(...this.unsignedpsbt.inputMap, end)

        // Output Map + 0x00
        //tmp.push(...this.unsignedpsbt.outputMap)
        this.unsignedpsbt.outputMap.length > 0 ? tmp.push(...this.unsignedpsbt.outputMap) : tmp.push(...this.unsignedpsbt.outputMap,end)

        this.hex = Buffer.from(tmp,'hex').toString('hex')
        this.base64 = Buffer.from(tmp).toString('base64')

        //console.log("[PSBT Unsigned]: ",Buffer.from(tmp).toString('hex'))
        return tmp
    }
    
    decode(buf) {
        /**
         * WIP
         * <keyLen> <keyType> <keyData?> <valueLen> <value>
         */
        let ptr = 0
        const bufLen = Buffer.from(buf,'hex').length

        console.log("[DECODE]: ", bufLen ,Buffer.from(buf.slice(0,4)).toString('hex'))

        if (!buf.slice(0,4).toString('hex') === "70736274") {return}

        if (buf.slice(0,4).toString('hex') === "70736274") {
            ptr += 5
        }
        
        while (ptr < bufLen) {
            //ptr += 1
            const read = Buffer.from(Uint8Array.from([buf[ptr]]),'hex')
            //console.log("[READ]: ",ptr, Buffer.from(read,'hex').toString('hex'))

            try {
                const keyLen = Number(read.toString('hex')) //parseInt(read.toString('hex'),16)
                ptr += 1
                const keyType = Buffer.from(Uint8Array.from([buf[ptr]]),'hex')
                if (keyType > 0x1c) {continue}
                const keyData = keyLen > 1 ? Buffer.from(Uint8Array.from([...buf.slice(ptr, ptr+keyLen)]),'hex') : null
                ptr += keyLen
                const valueLen = buf[ptr] //parseInt(Buffer.from(Uint8Array.from([buf[ptr]]),'hex').toString('hex'),16)
                ptr += 1
                const value = Buffer.from(Uint8Array.from([...buf.slice(ptr, ptr+valueLen)]),'hex')
                ptr += valueLen

                console.log("\n[KeyLen]: ", keyLen)
                console.log("[KeyType]: ", keyType.toString('hex'))
                console.log("[KeyData]: ", keyData)
                console.log("[ValueLen]: ", valueLen)
                console.log("[Value]: ", value.toString('hex'))
            } catch (error) {
                console.log("[ERROR]: psbt decode")
            }            

            ptr += 1
        }
        
    }

    toHex() {
        return this.hex
    }

    toBase64() {
        return this.base64
    }

    encodeHex(array) {
        const hex = [array.length,...array]

        // toString(16).padStart(4, '0')
        let hex2
        // if data < 75 bytes |--> data.len
        if (Buffer.from(array,'hex').length <= 75) {
            hex2 = Buffer.concat([
                Uint8Array.from([array.length]),
                Buffer.from(array,'hex')
            ])
            this.debugger && console.log("[DATA < 75]",array.length,Buffer.from(array,'hex').length.toString(16).padStart(2, '0'))
        }
        // if data > 75 < 253 |--> 0xfc
        if (Buffer.from(array,'hex').length > 75 && Buffer.from(array,'hex').length < 253 ) {
            hex2 = Buffer.concat([
                //Uint8Array.from([ 0xfc, Buffer.from(array,'hex').length.toString(16)]),
                Buffer.from(Buffer.from(array,'hex').length.toString(16),'hex'),
                Buffer.from(array,'hex')
            ])
            this.debugger && console.log("[75 < DATA < 253]",array.length,Buffer.from(array,'hex').length.toString(16))         
        }
        // if data > 253 |--> 0xfd 0x0100 (256bytes) || 0x0802 (520bytes)
        if (Buffer.from(array,'hex').length >= 253) {
            hex2 = Buffer.concat([
                Uint8Array.from([0xfd, ...Buffer.from(Buffer.from(array,'hex').length.toString(16).padStart(4, '0'),'hex').reverse()]),
                Buffer.from(array,'hex')
            ])
            this.debugger && console.log("[DATA > 253]", array.length,Buffer.from(array,'hex').length.toString(16).padStart(4, '0'))
        }

        if (Buffer.from(array,'hex').length > 520) {
            this.debugger && console.log("[***MAX_LENGTH***]")
            hex2 = Buffer.concat([
                Uint8Array.from([0xfd, ...Buffer.from(Buffer.from(array,'hex').length.toString(16).padStart(4, '0'),'hex').reverse()]),
                Buffer.from(array,'hex')
            ])
            //hex2 = []
        }
        
        //this.log("encodeHEX", Buffer.from(hex2).toString('hex'))
        return hex2
    }

    decodeHex(array) {
        const length = array.length
        let ptr = 0
        let buf = []

        while (ptr < length) {
            const op = array[ptr]
            if (ptr>length) {
                //this.log("PTR","END")
                return
            }
            if (op === 0x00) {
                //this.log("PTR","0x00")
                return
            }
            if (op > 0 || op < 0x4c) {
                const tmp = array.slice(ptr+1,op+1)
                //this.log("TMP",tmp)
                buf.push(...tmp)
                ptr += op +1
            }
        }
        //this.log("decodeHEX", Buffer.from(buf).toString('hex'))
        return buf
    }

    longToByteArray(long) {
        // we want to represent the input as a 8-bytes array
        var byteArray = [0, 0, 0, 0, 0, 0, 0, 0];
    
        for ( var index = 0; index < byteArray.length; index ++ ) {
            var byte = long & 0xff;
            byteArray [ index ] = byte;
            long = (long - byte) / 256 ;
        }
    
        return byteArray;
    }

    debug() {
        console.log("[GlobalMap]: ",Buffer.from(this.unsignedpsbt.globalMap).toString('hex'))
        console.log("[InputMap]: ",Buffer.from(this.unsignedpsbt.inputMap).toString('hex'))
        console.log("[OutputMap]: ",Buffer.from(this.unsignedpsbt.outputMap).toString('hex'))
        console.log("[HEX]: ", Buffer.from(this.hex,'hex').toString('hex'))
        console.log("[Base64]: ", this.base64)
    }
}

async function getInfo(id,url) {
    const api = await new API(id,url).get()
    return api
}


const main = async (type="p2sh",online=false,test=false) => {
    console.log("* * * BTC TX Builder * * *")

    const wallet = new Wallet()
    await wallet.importPrivateKey(Buffer.from("F000000000000000000000000000000000000000000000000000000000000001","hex")) // privateKey rawhex
    const address = await wallet.getAddress(type) // true for QRcode export

    const pubKey = await wallet.getPublicKey()
    
    //console.log(`[ADDRESS-${type}]: `,address)
    await wallet.sign(pubKey)

    let _txid = ""
    let _pubkey = Buffer.from(pubKey).toString('hex')
    let _amount = 5001
    let _to = address
    let _wit = "00"

    if (online) {
        const testnet = test?"testnet/":""
        try {
            //balance
            const status = await wallet.getApi("status",`https://mempool.space/${testnet}api/address/${address}`)
            //txs
            const txs = await wallet.getApi("utxo", `https://mempool.space/${testnet}api/address/${address}/txs`)
            //utxo
            const utxo = await wallet.getApi("txs", `https://mempool.space/${testnet}api/address/${address}/utxo`)
            //block
            const block = await wallet.getApi("block", `https://mempool.space/${testnet}api/blocks/tip/height`)
            //price
            const price = await wallet.getApi("price", `https://mempool.space/api/v1/prices`)

            //
            //const broadcast = https://mempool.space/testnet/api/tx //POST
            /*
            console.log("[STATUS]: ", status)
            console.log("[UTXO]: ", utxo)
            console.log("[TXS]: ", txs)
            console.log("[BLOCK]: ", block)
            console.log("[PRICE]: ", price)
            */
            _txid = txs[0].txid

        } catch (error) {
            console.log("[API] error: ",error)
        }
    }
    
    const tx = new TX("testnet",1,false)
    
    /**
     * -in txid + script pubkey hash
     * -out amount + address
     */
    const serializedTx = tx
    .createInput(_txid, _pubkey)
    .createOutput(_amount, _to)
    //.addWitness(_wit)
    .serialize()
    //tx.debug()
    //console.log(tx)
    console.log("==================")
    console.log(serializedTx)
    console.log("==================")

    const signature = await wallet.sign(serializedTx)

    //console.log("[SIGNATURE]: ", signature)

    //console.log(wallet)
}

/*
await main("p2sh-test",true,true) // address type, online, testnet
.then(()=>process.exit(0))
.catch((error)=>{
    console.error(error)
    process.exit(1)
})
*/
